/**
 * TON Wallet Service
 * Handles TON blockchain wallet connection and payments for ZiaWellNav
 * Uses TON Connect for Telegram Mini Apps integration
 * 
 * Charity: 10% monthly, 20% yearly to Packs for Hunger (ziagivesback.ton)
 */

import { TonConnectUI, SendTransactionRequest, CHAIN, Wallet } from '@tonconnect/ui';

// Simple toNano conversion (1 TON = 10^9 nanoTON)
function toNano(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Math.floor(num * 1_000_000_000).toString();
}

// Simple fromNano conversion
function fromNano(amount: string | bigint): string {
  const num = typeof amount === 'bigint' ? amount : BigInt(amount);
  return (Number(num) / 1_000_000_000).toFixed(9);
}

// TON Connect manifest URL
const MANIFEST_URL = 'https://ziawellnav.pages.dev/tonconnect-manifest.json';

// Charity wallet address (ziagivesback.ton - will be resolved)
const CHARITY_WALLET_RAW = 'UQBxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'; // Update after DNS resolution

// Platform wallet for receiving payments
const PLATFORM_WALLET = 'UQBxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'; // Set your wallet address

// Subscription prices in TON (approximate USD conversions)
export const TON_PRICES: Record<string, { price: number; charityPercent: number; description: string }> = {
  // New plan IDs
  individual_monthly: {
    price: 3.5, // ~$10 USD in TON
    charityPercent: 10,
    description: 'Monthly subscription'
  },
  individual_yearly: {
    price: 35, // ~$100 USD in TON (2 months free)
    charityPercent: 20,
    description: 'Annual subscription - 20% to charity!'
  },
  business_starter: {
    price: 7, // $7/user, can multiply by licenses
    charityPercent: 20,
    description: 'Business Starter (5-25 users)'
  },
  business_team: {
    price: 6, // $6/user
    charityPercent: 20,
    description: 'Business Team (26-100 users)'
  },
  business_fleet: {
    price: 5, // $5/user
    charityPercent: 20,
    description: 'Business Fleet (101-250 users)'
  },
  business_enterprise: {
    price: 4, // $4/user
    charityPercent: 20,
    description: 'Business Enterprise (251+ users)'
  },
  // Legacy support
  monthly: {
    price: 3.5,
    charityPercent: 10,
    description: 'Monthly subscription'
  },
  yearly: {
    price: 35,
    charityPercent: 20,
    description: 'Yearly subscription - 20% to charity!'
  },
  business_plus: {
    price: 70,
    charityPercent: 30,
    description: 'Business Plus - 30% to charity!'
  }
};

// Service marketplace fee
export const SERVICE_FEE_PERCENT = 10;

let tonConnectUI: TonConnectUI | null = null;
let connectionPromise: Promise<TonConnectUI> | null = null;

/**
 * Initialize TON Connect UI
 */
export async function initTonConnect(): Promise<TonConnectUI> {
  if (tonConnectUI) return tonConnectUI;
  
  if (connectionPromise) return connectionPromise;
  
  connectionPromise = (async () => {
    tonConnectUI = new TonConnectUI({
      manifestUrl: MANIFEST_URL,
      buttonRootId: 'ton-connect-button',
      uiPreferences: {
        theme: 'SYSTEM'
      }
    });
    
    // Restore connection if previously connected
    await tonConnectUI.connectionRestored;
    
    return tonConnectUI;
  })();
  
  return connectionPromise;
}

/**
 * Get current wallet connection status
 */
export async function getWalletStatus(): Promise<{
  connected: boolean;
  address: string | null;
  chain: string | null;
  wallet: Wallet | null;
}> {
  const tc = await initTonConnect();
  const wallet = tc.wallet;
  
  if (!wallet) {
    return { connected: false, address: null, chain: null, wallet: null };
  }
  
  return {
    connected: true,
    address: wallet.account.address,
    chain: wallet.account.chain === CHAIN.MAINNET ? 'mainnet' : 'testnet',
    wallet
  };
}

/**
 * Connect wallet (opens modal)
 */
export async function connectWallet(): Promise<string | null> {
  const tc = await initTonConnect();
  
  if (tc.wallet) {
    return tc.wallet.account.address;
  }
  
  try {
    await tc.openModal();
    
    // Wait for connection
    return new Promise((resolve) => {
      const unsubscribe = tc.onStatusChange((wallet) => {
        if (wallet) {
          unsubscribe();
          resolve(wallet.account.address);
        }
      });
      
      // Timeout after 5 minutes
      setTimeout(() => {
        unsubscribe();
        resolve(null);
      }, 300000);
    });
  } catch (error) {
    console.error('Failed to connect wallet:', error);
    return null;
  }
}

/**
 * Disconnect wallet
 */
export async function disconnectWallet(): Promise<void> {
  const tc = await initTonConnect();
  await tc.disconnect();
}

/**
 * Format wallet address for display (shortened)
 */
export function formatAddress(address: string): string {
  if (!address) return '';
  // Simple shortening without @ton/ton Address parsing
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Convert TON to nanoTON (1 TON = 10^9 nanoTON)
 */
export function tonToNano(amount: number): string {
  return toNano(amount);
}

/**
 * Convert nanoTON to TON
 */
export function nanoToTon(amount: string | bigint): number {
  return parseFloat(fromNano(amount));
}

/**
 * Create payment comment as base64 payload
 * TON comment format: 0x00000000 (4 bytes) + UTF-8 text
 */
function createPaymentPayload(text: string): string {
  // Convert text to bytes with comment prefix (4 zero bytes)
  const textBytes = new TextEncoder().encode(text);
  const payload = new Uint8Array(4 + textBytes.length);
  // First 4 bytes are 0 (comment op code)
  payload.set(textBytes, 4);
  // Convert to base64
  return btoa(String.fromCharCode(...payload));
}

/**
 * Pay for subscription with TON
 * Splits payment: charity portion + platform portion
 */
export async function paySubscription(
  plan: string,
  userId: string
): Promise<{ success: boolean; txHash?: string; error?: string }> {
  const tc = await initTonConnect();
  
  if (!tc.wallet) {
    return { success: false, error: 'Wallet not connected' };
  }
  
  const pricing = TON_PRICES[plan];
  const totalTon = pricing.price;
  const charityTon = (totalTon * pricing.charityPercent) / 100;
  const platformTon = totalTon - charityTon;
  
  // Create transaction with multiple messages (charity + platform)
  const transaction: SendTransactionRequest = {
    validUntil: Math.floor(Date.now() / 1000) + 600, // 10 minutes
    network: CHAIN.MAINNET,
    messages: [
      // Charity payment (ziagivesback.ton)
      {
        address: CHARITY_WALLET_RAW,
        amount: tonToNano(charityTon),
        payload: createPaymentPayload(`ZiaWellNav ${plan} - Packs for Hunger`)
      },
      // Platform payment
      {
        address: PLATFORM_WALLET,
        amount: tonToNano(platformTon),
        payload: createPaymentPayload(`ZiaWellNav ${plan} subscription - User: ${userId}`)
      }
    ]
  };
  
  try {
    const result = await tc.sendTransaction(transaction);
    
    // Verify transaction on backend
    const verification = await verifyPaymentOnBackend({
      boc: result.boc,
      plan,
      userId,
      expectedAmount: totalTon,
      charityAmount: charityTon
    });
    
    if (verification.success) {
      return { success: true, txHash: verification.txHash };
    } else {
      return { success: false, error: verification.error || 'Payment verification failed' };
    }
  } catch (error: any) {
    if (error.message?.includes('User rejected')) {
      return { success: false, error: 'Transaction cancelled by user' };
    }
    console.error('Transaction failed:', error);
    return { success: false, error: error.message || 'Transaction failed' };
  }
}

/**
 * Pay for service with escrow (job marketplace)
 * 10% platform fee deducted when service is completed
 */
export async function payForService(
  serviceId: string,
  amount: number,
  recipientAddress: string,
  description: string
): Promise<{ success: boolean; txHash?: string; error?: string }> {
  const tc = await initTonConnect();
  
  if (!tc.wallet) {
    return { success: false, error: 'Wallet not connected' };
  }
  
  // For escrow, we send to platform wallet first
  // Platform releases to recipient minus fee when service is completed
  const transaction: SendTransactionRequest = {
    validUntil: Math.floor(Date.now() / 1000) + 600,
    network: CHAIN.MAINNET,
    messages: [
      {
        address: PLATFORM_WALLET,
        amount: tonToNano(amount),
        payload: createPaymentPayload(`Escrow: ${serviceId} - ${description}`)
      }
    ]
  };
  
  try {
    const result = await tc.sendTransaction(transaction);
    
    // Register escrow on backend
    const registration = await registerEscrowOnBackend({
      boc: result.boc,
      serviceId,
      amount,
      recipientAddress,
      description
    });
    
    return registration;
  } catch (error: any) {
    if (error.message?.includes('User rejected')) {
      return { success: false, error: 'Transaction cancelled by user' };
    }
    return { success: false, error: error.message || 'Transaction failed' };
  }
}

/**
 * Verify payment on backend
 */
async function verifyPaymentOnBackend(data: {
  boc: string;
  plan: string;
  userId: string;
  expectedAmount: number;
  charityAmount: number;
}): Promise<{ success: boolean; txHash?: string; error?: string }> {
  try {
    const response = await fetch('/api/ton/verify-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    return await response.json();
  } catch (error) {
    console.error('Backend verification failed:', error);
    return { success: false, error: 'Failed to verify payment' };
  }
}

/**
 * Register escrow on backend
 */
async function registerEscrowOnBackend(data: {
  boc: string;
  serviceId: string;
  amount: number;
  recipientAddress: string;
  description: string;
}): Promise<{ success: boolean; txHash?: string; error?: string }> {
  try {
    const response = await fetch('/api/ton/register-escrow', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    return await response.json();
  } catch (error) {
    console.error('Escrow registration failed:', error);
    return { success: false, error: 'Failed to register escrow' };
  }
}

/**
 * Get charity wallet balance (for transparency display)
 */
export async function getCharityBalance(): Promise<{ balance: number; error?: string }> {
  try {
    const response = await fetch('/api/ton/charity-wallet');
    const data = await response.json();
    return { balance: data.balance || 0 };
  } catch (error) {
    console.error('Failed to get charity balance:', error);
    return { balance: 0, error: 'Failed to fetch balance' };
  }
}

/**
 * Get recent charity donations (for transparency)
 */
export async function getRecentCharityDonations(): Promise<{
  donations: Array<{
    amount: number;
    timestamp: string;
    txHash: string;
    type: 'monthly' | 'yearly';
  }>;
  total: number;
}> {
  try {
    const response = await fetch('/api/ton/charity-donations');
    return await response.json();
  } catch (error) {
    console.error('Failed to get charity donations:', error);
    return { donations: [], total: 0 };
  }
}

/**
 * Link wallet address to user account
 */
export async function linkWalletToUser(userId: string): Promise<{ success: boolean; error?: string }> {
  const tc = await initTonConnect();
  
  if (!tc.wallet) {
    return { success: false, error: 'Wallet not connected' };
  }
  
  try {
    const response = await fetch('/api/ton/link-wallet', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        walletAddress: tc.wallet.account.address
      })
    });
    
    return await response.json();
  } catch (error) {
    console.error('Failed to link wallet:', error);
    return { success: false, error: 'Failed to link wallet' };
  }
}

/**
 * Subscribe to wallet connection status changes
 */
export function onWalletStatusChange(callback: (connected: boolean, address: string | null) => void): () => void {
  let unsubscribe: (() => void) | null = null;
  
  initTonConnect().then((tc) => {
    unsubscribe = tc.onStatusChange((wallet) => {
      callback(!!wallet, wallet?.account.address || null);
    });
  });
  
  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

export default {
  initTonConnect,
  getWalletStatus,
  connectWallet,
  disconnectWallet,
  formatAddress,
  tonToNano,
  nanoToTon,
  paySubscription,
  payForService,
  getCharityBalance,
  getRecentCharityDonations,
  linkWalletToUser,
  onWalletStatusChange,
  TON_PRICES,
  SERVICE_FEE_PERCENT
};
